import * as React from 'react';

import {Button} from 'shells/button/button';
import {CalendarSquareRegularTypeIcon} from 'shells/icon/svg/calendar-square-regular-type-icon';
import {LeadIcon} from 'shells/icon/svg/lead-icon';
import {IconSection} from 'shells/icon-section/icon-section';
import type {SavedSettingToastState} from 'shells/saved-setting-toast/saved-setting-toast';
import {SelectionList} from 'shells/selection-modal/selection-list';
import {Body} from 'shells/typography/body';

import type {GetPotentialActivityEntities_activityPotentialEntities_relatedEntities as RelatedEntity} from 'nutshell-graphql-types';

import {GraphQLSelectActivityTypes} from '../../../core/select/graphql-select-activity-types';
import type {Option} from '../../../core/select/graphql-select-inline';
import {GraphQLSelectLeads} from '../../../core/select/graphql-select-leads';
import type {SelectOption} from '../../../setup/import/dromo/types';

import {mapOptionToValue, mapValueToOption} from './helpers';

import styles from './internal-notes.module.css';

type Props = {
    currentActivityType: SelectOption | null | undefined;
    onActivityTypeChange: (selectedOption: SelectOption) => void;
    activityTypeSaveState?: SavedSettingToastState;
    setActivityTypeSaveState?: (state: SavedSettingToastState) => void;
    selectedRelatedEntities: Option[];
    onRelatedLeadsChange: (selectedValues: Option[]) => void;
    potentialRelatedEntities?: RelatedEntity[] | null | undefined;
    relatedLeadsSaveState?: SavedSettingToastState;
    setRelatedLeadsSaveState?: (state: SavedSettingToastState) => void;
};

export function ActivityTypeAndLeadsSection(props: Props) {
    const [isRelatedLeadsOpen, setIsRelatedLeadsOpen] = React.useState<boolean>(false);

    const defaultOptions = props.potentialRelatedEntities ? props.potentialRelatedEntities : [];

    const addLeadButton = (
        <Button variant='text-primary' onClick={() => setIsRelatedLeadsOpen(true)} noPadding={true}>
            + Add a lead
        </Button>
    );

    return (
        <div className={styles['activity-type-and-leads-section-container']}>
            <IconSection
                icon={CalendarSquareRegularTypeIcon}
                fullWidth={true}
                marginTopValue={0}
                header='Activity type'
                saveState={props.activityTypeSaveState}
                setSaveState={props.setActivityTypeSaveState}
            >
                <GraphQLSelectActivityTypes
                    selectedId={props.currentActivityType && props.currentActivityType.value}
                    onChange={props.onActivityTypeChange}
                    minHeight={36}
                    showSelectedValueTitle={true}
                />
            </IconSection>
            <IconSection
                icon={LeadIcon}
                fullWidth={true}
                marginTopValue={0}
                saveState={props.relatedLeadsSaveState}
                setSaveState={props.setRelatedLeadsSaveState}
                header='Related leads'
                headerButton={!isRelatedLeadsOpen ? addLeadButton : null}
            >
                <div className={styles['related-leads-section-container']}>
                    {isRelatedLeadsOpen && (
                        <GraphQLSelectLeads
                            selectedValues={props.selectedRelatedEntities}
                            onChange={props.onRelatedLeadsChange}
                            isMulti={true}
                            searchable={true}
                            defaultOptions={defaultOptions.length !== 0 ? defaultOptions : true}
                            controlShouldRenderValue={false}
                            onBlur={() => setIsRelatedLeadsOpen(false)}
                            autoFocus={true}
                        />
                    )}
                    {props.selectedRelatedEntities.length > 0 && (
                        <SelectionList
                            selectedOptions={mapValueToOption(props.selectedRelatedEntities)}
                            setSelectedOptions={(options) => {
                                props.onRelatedLeadsChange(mapOptionToValue(options));
                            }}
                            nothingFoundState=''
                            hasBottomBorder={false}
                            shouldListFitContent={true}
                            smallLabel={true}
                            maxDisplayedBeforeSeeMore={4}
                        />
                    )}
                    {props.selectedRelatedEntities.length === 0 && !isRelatedLeadsOpen && (
                        <div className={styles['related-leads-list-empty-state']}>
                            <Body size='small' color='light'>
                                No related leads
                            </Body>
                        </div>
                    )}
                </div>
            </IconSection>
        </div>
    );
}
